import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import "../styles/medicationList.css"; // Import stylesheet
import { SERVER_URL } from "../config";

interface Medication {
  id: number;
  name: string;
  type: string;
  brand: string;
  active: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

const MedicationList: React.FC = () => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/medication/meds`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.statusText === "Unauthorized") {
          navigate("/");
        }

        if (!response.ok) {
          throw new Error("Failed to fetch medications.");
        }

        const data: Medication[] = await response.json();
        setMedications(data);
      } catch (err) {
        console.error("Error fetching medications:", err);
        setError("Failed to fetch medication data.");
      } finally {
        setLoading(false);
      }
    };

    fetchMedications();
  }, []);

  if (loading) {
    return <p className="loading-message">Loading medications...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  return (
    <div className="medication-list-container">
      <h2>Medication List</h2>
      {medications.length === 0 ? (
        <p className="error-message">No medications available.</p>
      ) : (
        <table className="medication-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Brand</th>
              <th>Active</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {medications.map((medication) => (
              <tr key={medication.id}>
                <td className="medication-cell">{medication.name}</td>
                <td className="medication-cell">{medication.type}</td>
                <td className="medication-cell">{medication.brand}</td>
                <td className="medication-cell">
                  {medication.active ? "Yes" : "No"}
                </td>
                <td className="medication-cell">
                  {medication.description || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MedicationList;
